import { from, Observable } from "rxjs";

import { autoBind } from "src/shared/decorators/autobind";
import {
    DeliveryBasicData,
    IMedicineDeliveryDocumentRepository,
    IMedicineDeliverytRepository,
    MedicineDeliveryRequestIdResponse,
} from "src/domains/medicine-delivery/types";
import type { HttpRequestClient } from "src/infra/http/types";
import type { AttentionType } from "src/domains/medicine-delivery/presentation/pages/basic-data/types";
import { getClinicsFallback } from "src/domains/medicine-delivery/libs/get-clinics";
import { MedicineDeliveryEndpoints } from "src/domains/medicine-delivery/constants/endpoints";
import { httpUtils } from "src/infra/http/utils";
import { Environment } from "src/libs/environment";

export interface RequestProps {
    insuredApplication: string;
    insuranceRequest: number;
    clinicId: string;
    attentionDate: string;
    receivedClinicalMedication: boolean;
    casesContinuity: boolean;
    travelMedicine: boolean;
    tripStartDate: string;
    tripEndDate: string;
}

export class MedicineDeliveryRepository
    implements IMedicineDeliverytRepository
{
    constructor(
        private http: HttpRequestClient,
        private docRepo: IMedicineDeliveryDocumentRepository
    ) {}

    @autoBind
    getRequestId(
        data: Partial<DeliveryBasicData>,
        systemCode: string
    ): Observable<MedicineDeliveryRequestIdResponse> {
        const endpoint = MedicineDeliveryEndpoints.GetRequestId;
        return this.http.post<MedicineDeliveryRequestIdResponse>(endpoint, {
            isPublicRequest: false,
            data,
            params: {
                "system-code": systemCode,
            },
            customHeader: {
                ...httpUtils.getBaseHeaders(),
                ...httpUtils.getMsBaseHeaders(window.userIdentity, "MEP"),
            },
        });
    }

    @autoBind
    getClinics(
        productCode: string,
        attentionType: AttentionType
    ): Observable<Record<string, any>[]> {
        return from(
            getClinicsFallback(productCode, attentionType) as unknown as Record<
                string,
                any
            >[]
        ) as Observable<Record<string, any>[]>;
        // Ignore endpoint for now
        const endpoint = MedicineDeliveryEndpoints.GetClinics;
        return this.http.getJSON<Record<string, any>[]>(endpoint, {
            isPublicRequest: false,
            params: {
                "product-code": productCode,
                "attention-type": attentionType,
            },
            customHeader: {
                ...httpUtils.getBaseHeaders(),
                ...httpUtils.getMsBaseHeaders(window.userIdentity, "MEP"),
            },
        });
    }

    @autoBind
    getActiveRequests(): Observable<Record<string, unknown>[]> {
        const endpoint = MedicineDeliveryEndpoints.GetActiveRequests;
        return this.http.getJSON<Record<string, unknown>[]>(endpoint, {
            host: Environment.string("APIMGMT_URL"),
            isPublicRequest: false,
            customHeader: {
                ...httpUtils.getBaseHeaders(),
                ...httpUtils.getMsBaseHeaders(window.userIdentity, "MEP"),
            },
        });
    }

    @autoBind
    getDocuments(requestId: number): Observable<Record<string, any>[]> {
        return this.docRepo.getDocuments(requestId);
    }

    @autoBind
    uploadDocument(
        requestId: number,
        file: File
    ): Observable<Record<string, any>> {
        const data = new FormData();
        data.append("file", file);
        return this.docRepo.uploadDocument(requestId, data);
    }

    @autoBind
    removeDocument(requestId: number, fileId: number): Observable<void> {
        return this.docRepo.removeDocument(requestId, fileId);
    }
}
